/**
 * Quantity input component with validation
 * Handles quantity updates for cart line items with inventory and quantity rule validation
 */

import { globalEmitter, emitterProps } from 'helpers/emitter-event'
import { QuantityRule } from '@/components/helpers/cart.types'
import { cart, Cart } from '@/components/helpers/cart'
import { ShopifySectionHelper } from '../helpers/section-render'
import { globalState } from '@/components/helpers/utils'

export class QuantityInput extends HTMLElement {
  private quantity: number = 1
  private inventoryQuantity: number | null = null
  private quantityRule: QuantityRule | null = null
  private error: string | null = null
  private isLoading: boolean = false
  private errorTimeout: NodeJS.Timeout | null = null
  private line: number | null = null

  // Elements
  private minusButton: HTMLElement | null = null
  private plusButton: HTMLElement | null = null
  private quantityInput: HTMLInputElement | null = null
  private errorDisplay: HTMLElement | null = null

  constructor() {
    super()
    this.handleQuantityChange = this.handleQuantityChange.bind(this)
  }

  static get observedAttributes() {
    return ['inventory-quantity', 'quantity', 'quantity-rule', 'line']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return

    switch (name) {
      case 'inventory-quantity':
        this.inventoryQuantity = newValue ? parseInt(newValue) : null
        break
      case 'quantity':
        this.quantity = newValue ? parseInt(newValue) : 1
        this.updateQuantityDisplay()
        break
      case 'quantity-rule':
        try {
          this.quantityRule = newValue ? JSON.parse(newValue) : null
        } catch (e) {
          console.error('Invalid quantity rule format:', e)
        }
        break
      case 'line':
        this.line = newValue ? parseInt(newValue) : null
        break
    }
  }

  connectedCallback() {
    this.setupElements()
    this.addEventListeners()
    this.updateButtonStates()
  }

  disconnectedCallback() {
    this.removeEventListeners()
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
  }

  private setupElements() {
    this.minusButton = this.querySelector('[data-quantity-minus]')
    this.plusButton = this.querySelector('[data-quantity-plus]')
    this.quantityInput = this.querySelector('[data-quantity-input]')
    this.errorDisplay = this.querySelector('[data-quantity-error]')

    // Set initial quantity if provided via input
    if (this.quantityInput && this.quantityInput.value) {
      this.quantity = parseInt(this.quantityInput.value) || 1
    }
  }

  private handleMinusClick = (e: Event) => this.handleQuantityChange(e, 'minus')
  private handlePlusClick = (e: Event) => this.handleQuantityChange(e, 'plus')

  private addEventListeners() {
    this.minusButton?.addEventListener('click', this.handleMinusClick)
    this.plusButton?.addEventListener('click', this.handlePlusClick)
    this.quantityInput?.addEventListener('change', this.handleInputChange)
  }

  private removeEventListeners() {
    this.minusButton?.removeEventListener('click', this.handleMinusClick)
    this.plusButton?.removeEventListener('click', this.handlePlusClick)
    this.quantityInput?.removeEventListener('change', this.handleInputChange)
  }

  private handleInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement
    const newQuantity = parseInt(input.value)
    if (!isNaN(newQuantity)) {
      this.handleQuantityUpdate(newQuantity)
    }
  }

  private validateQuantity(newQuantity: number): boolean {
    // Check inventory limit
    if (this.inventoryQuantity !== null && newQuantity > this.inventoryQuantity) {
      this.setError('Maximum quantity reached')
      return false
    }

    // Check quantity rules if they exist
    if (this.quantityRule) {
      if (newQuantity < this.quantityRule.min) {
        this.setError(`Minimum quantity is ${this.quantityRule.min}`)
        return false
      }
      if (this.quantityRule.max && newQuantity > this.quantityRule.max) {
        this.setError(`Maximum quantity is ${this.quantityRule.max}`)
        return false
      }
    }

    return true
  }

  private async handleQuantityChange(e: Event, action: 'plus' | 'minus') {
    e.preventDefault()
    const newQuantity = this.calculateNewQuantity(action)
    await this.handleQuantityUpdate(newQuantity)
  }

  private async handleQuantityUpdate(newQuantity: number) {
    try {
      this.setLoading(true)

      if (!this.validateQuantity(newQuantity)) {
        this.setLoading(false)
        return
      }

      if (this.line && cart) {
        Cart.skipNextCartPageRefresh = true
        const data = await cart.changeLineItem(
          {
            line: this.line,
            quantity: newQuantity
          },
          { silent: true }
        )

        if (data.error) {
          this.setError(data.error)
          return
        }

        const changeType = data.items_added.length > 0 ? 'plus' : data.items_removed.length > 0 ? 'minus' : null
        const changedItem =
          data.items_added.length > 0
            ? data.items_added[0]
            : data.items_removed.length > 0
              ? data.items_removed[0]
              : null
        if (changeType && changedItem) {
          this.quantity = this.quantity + (changeType === 'plus' ? changedItem.quantity : -changedItem.quantity)
        }
      }
      await ShopifySectionHelper.fetchAndUpdatePage({
        url: globalState?.routes?.cart_url || '/cart',
        fetchSelectors: ['#cart-items-inner', '#cart-summary'],
        targetSelectors: ['#cart-items-inner', '#cart-summary']
      })
      window.lazyLoadInstance?.update()
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
      })

      // Dispatch custom event
      this.dispatchEvent(
        new CustomEvent('quantity-changed', {
          detail: { quantity: newQuantity },
          bubbles: true
        })
      )
    } catch (error) {
      if (error instanceof Error) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: error.message
        })
      }
    } finally {
      this.setLoading(false)
    }
  }

  private calculateNewQuantity(action: 'plus' | 'minus'): number {
    switch (action) {
      case 'plus':
        return this.quantity + 1
      case 'minus':
        return this.quantity - 1
      default:
        return this.quantity
    }
  }

  private updateButtonStates() {
    // Disable minus button if at minimum
    if (this.minusButton) {
      const isAtMin = this.quantity <= (this.quantityRule?.min || 0)
      this.minusButton.toggleAttribute('disabled', isAtMin)
    }

    // Disable plus button if at maximum inventory
    if (this.plusButton) {
      const isAtMax = this.inventoryQuantity !== null && this.quantity >= this.inventoryQuantity
      this.plusButton.toggleAttribute('disabled', isAtMax)
    }
  }

  private setError(message: string) {
    this.error = message
    if (this.errorDisplay) {
      this.errorDisplay.textContent = message
      this.errorDisplay.classList.remove('opacity-0', 'max-h-0')
      this.errorDisplay.classList.add('opacity-100', 'max-h-[18px]')
    }

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
    this.errorTimeout = setTimeout(() => this.clearError(), 5000)
  }

  private clearError() {
    this.error = null
    if (this.errorDisplay) {
      this.errorDisplay.classList.add('opacity-0', 'max-h-0')
      this.errorDisplay.classList.remove('opacity-100', 'max-h-[18px]')
    }
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading
    if (loading) {
      this.classList.add('opacity-45')
    } else {
      this.classList.remove('opacity-45')
    }
  }

  private updateQuantityDisplay() {
    if (this.quantityInput) {
      this.quantityInput.value = this.quantity.toString()
    }
  }
}
